"use client";

import { useCallback, type MouseEvent } from "react";
import { flashCite } from "@/components/page/citeFlash";

type Props = {
  /**
   * The figure-scoped id this citation lives under. Must match the
   * `figureId` passed to the matching `<SourceSidenote>`. Pages with
   * multiple stacked sidenotes (e.g. /climate/hazards) need this so
   * each figure's `[1]` resolves to its own sidenote entry.
   */
  figureId: string;
  /** 1-based source index, matching the order in `meta.sources`. */
  n: number;
  /**
   * Render style. `inline` is the default — a small superscript-style
   * `[n]` that sits inside body prose. `marker` is the same shape but
   * baseline-aligned for use inside table cells / chart labels where a
   * superscript would clip.
   */
  variant?: "inline" | "marker";
};

/**
 * Inline citation marker that scroll-links to a numbered entry in a
 * `<SourceSidenote>` rail. Authoring convention:
 *
 *   The strategy projects $60–80 billion in cumulative savings <Cite figureId="claims-pathways" n={1} />.
 *
 * On click: smooth-scrolls the matching `<li id="cite-{figureId}-{n}">`
 * into view, focuses it, and runs a short pulse so the reader's eye
 * lands on the right entry. Honours `prefers-reduced-motion`. The
 * underlying `<a href="#cite-…">` keeps middle-click / right-click /
 * keyboard activation working as ordinary anchor behaviour, and the
 * page is still deep-linkable to a citation (`:target` CSS in
 * globals.css triggers the pulse on direct hash navigation).
 */
export function Cite({ figureId, n, variant = "inline" }: Props) {
  const id = `cite-${figureId}-${n}`;

  const onClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      // Let modifier-clicks (cmd/ctrl/shift, middle-button) fall through
      // so users can open the anchor in a new tab or window normally.
      if (e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      flashCite(target, { scroll: true });
    },
    [id],
  );

  return (
    <a
      href={`#${id}`}
      onClick={onClick}
      className={variant === "inline" ? "cs-cite cs-cite-sup" : "cs-cite"}
      aria-label={`Jump to source ${n}`}
    >
      [{n}]
    </a>
  );
}
